import type { MarkerFeature } from "../../../../../utils/models/MarkerFeature";
import type { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import type { UndoCommand, CommandContext } from "../types";

/**
 * Command for removing a marker from a route.
 * Captures the removed marker and affected linestrings for restoration.
 */
export class RemoveMarkerCommand implements UndoCommand {
  readonly type = 'REMOVE_MARKER';
  readonly routeKey: string;

  private markerIndex: number;
  private removedMarker: MarkerFeature;
  /** Snapshot of lineStrings array before removal (only the affected portion) */
  private lineStringsBefore: LineStringFeature[];
  /** Snapshot of lineStrings array after removal (only the affected portion) */
  private lineStringsAfter: LineStringFeature[];
  /** Index of the first affected linestring */
  private lineStringsStartIndex: number;
  /** How many linestrings were at that position before removal */
  private lineStringsBeforeCount: number;

  constructor(data: {
    routeKey: string;
    markerIndex: number;
    removedMarker: MarkerFeature;
    lineStringsBefore: LineStringFeature[];
    lineStringsAfter: LineStringFeature[];
    lineStringsStartIndex: number;
    lineStringsBeforeCount: number;
  }) {
    this.routeKey = data.routeKey;
    this.markerIndex = data.markerIndex;
    this.removedMarker = JSON.parse(JSON.stringify(data.removedMarker));
    this.lineStringsBefore = JSON.parse(JSON.stringify(data.lineStringsBefore));
    this.lineStringsAfter = JSON.parse(JSON.stringify(data.lineStringsAfter));
    this.lineStringsStartIndex = data.lineStringsStartIndex;
    this.lineStringsBeforeCount = data.lineStringsBeforeCount;
  }

  undo(ctx: CommandContext): void {
    const route = ctx.state.routes[this.routeKey];
    if (!route) return;

    // Restore linestrings to before state
    route.lineStrings.splice(
      this.lineStringsStartIndex,
      this.lineStringsAfter.length,
      ...JSON.parse(JSON.stringify(this.lineStringsBefore))
    );

    // Re-insert the marker
    route.markers.splice(this.markerIndex, 0, JSON.parse(JSON.stringify(this.removedMarker)));

    route.updated_at = new Date();
  }

  redo(ctx: CommandContext): void {
    const route = ctx.state.routes[this.routeKey];
    if (!route) return;

    // Remove the marker
    route.markers.splice(this.markerIndex, 1);

    // Replace linestrings with the after state
    route.lineStrings.splice(
      this.lineStringsStartIndex,
      this.lineStringsBeforeCount,
      ...JSON.parse(JSON.stringify(this.lineStringsAfter))
    );

    route.updated_at = new Date();
  }
}
